import { useEffect, useState } from 'react';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { BaseButton, BaseDivider, BaseInput, BaseRow, LocaleSwitcher } from '@components';
import { HintText, InputLabel, InputWrapper } from '@components/input/styled.tsx';
import ClientLogo from '@components/logo';
import { ALERT_STATUSES, AlertNotifications } from '@constants/alertNotifications.ts';
import { INTERNAL_SERVER_ERROR_STATUS_CODE } from '@constants/api.ts';
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks.ts';
import { useCustomNavigate } from '@hooks/useCustomNavigate.ts';
import FooterLogo from '@layouts/auth/footer-logo';
import { useLingui } from '@lingui/react/macro';
import { ROUTES } from '@router/constants.ts';
import { getFieldError, getUpdatedFormFieldsWithErrors, isFieldErroneous } from '@routes/auth/___shared___/helpers.ts';
import ResetPasswordModal from '@routes/auth/___shared___/reset-password-modal';
import { renderChangePasswordInput } from '@routes/auth/___shared___/utils.tsx';
import { checkNotActivatedUserStatus, setEmail, setUserType } from '@routes/auth/register/model/register.slice.ts';
import { isUserAlreadyRegistered } from '@routes/auth/register/utils.ts';
import { clearAlert, setAlert } from '@shared/model/alert.slice.ts';
import { persistRegistrationUserEmail } from '@store/services/localStorage.service.ts';
import type { FormField } from '@types';
import { renderAlertErrorDesc } from '@utils/general.tsx';
import { isEmailValid } from '@utils/validation.ts';
import { Form } from 'antd';

import {
    AuthSharedWrapper,
    FooterLogoSection,
    FormSection,
    InvalidEmailErrorMsg,
    PasswordSection,
} from '../___shared___/styled.tsx';

interface RegistrationLocationState {
    from: string;
    tempPassword?: string;
}

const Register = () => {
    const { t } = useLingui();
    const LOGIN_FIELD_NAME = 'login';
    const PASSWORD_FIELD_NAME = 'password';
    const LOGIN_LABEL = t`Login`;
    const PASSWORD_LABEL = t`Password`;
    const LOGIN_ERROR_MSG = t`Please enter a correct email address or user ID.`;
    const USER_ALREADY_REGISTERED_ERROR_MSG = t`You are already registered.`;
    const EMAIL_REQUIRE_MSG = t`Email cannot be empty.`;
    const EMAIL_VALID_ERROR_MSG = t`Please enter a valid email address.`;
    const PASSWORD_REQUIRED_MSG = t`Password cannot be empty.`;

    const [isPasswordType, setIsPasswordType] = useState({ [PASSWORD_FIELD_NAME]: true });
    const [loginValue, setLoginValue] = useState('');
    const [formFields, setFormFields] = useState<FormField[]>([]);
    const [isResetModalOpen, setIsResetModalOpen] = useState(false);
    const [isValidLogin, setIsValidLogin] = useState(true);
    const isCheckNotActivatedUserStatusPending = useAppSelector(
        (state) => state.authRegister.isCheckNotActivatedUserStatusPending,
    );
    const navigate = useCustomNavigate();
    const dispatch = useAppDispatch();
    const [form] = Form.useForm();
    const loginHasError = isFieldErroneous(LOGIN_FIELD_NAME, formFields);

    const onFieldsChange = (_: any[], allFields: any[]) => setFormFields(allFields as FormField[]);

    const handleAlreadyRegisteredUserStatusError = () => {
        const { updatedFields } = getUpdatedFormFieldsWithErrors(formFields, [
            { name: LOGIN_FIELD_NAME, message: USER_ALREADY_REGISTERED_ERROR_MSG },
        ]);
        setFormFields(updatedFields);
    };

    const handleOtherUserStatusError = () => {
        const { updatedFields } = getUpdatedFormFieldsWithErrors(formFields, [
            { name: LOGIN_FIELD_NAME, message: LOGIN_ERROR_MSG },
            { name: PASSWORD_FIELD_NAME, message: t`Password incorrect. Please re-enter password` },
        ]);
        setFormFields(updatedFields);
    };

    const handleSubmit = () => {
        form.validateFields().then(({ login, password }) => {
            dispatch(
                checkNotActivatedUserStatus({
                    email: login,
                    password: password,
                }),
            )
                .then(() => {
                    dispatch(setEmail(loginValue));
                    persistRegistrationUserEmail(loginValue);
                    navigate(ROUTES.AUTH.RESET_PASSWORD, {
                        state: { from: ROUTES.AUTH.REGISTRATION, tempPassword: password } as RegistrationLocationState,
                    });
                })
                .catch((err) => {
                    const alreadyRegistered = isUserAlreadyRegistered(err);

                    if (alreadyRegistered) {
                        handleAlreadyRegisteredUserStatusError();
                    } else {
                        handleOtherUserStatusError();
                    }

                    if (err.status < INTERNAL_SERVER_ERROR_STATUS_CODE) {
                        dispatch(clearAlert()); //skip banner
                    } else {
                        dispatch(
                            setAlert({
                                type: ALERT_STATUSES.ERROR,
                                message: AlertNotifications[ALERT_STATUSES.ERROR].message,
                                description: renderAlertErrorDesc(err.status, err.statusText),
                            }),
                        );
                    }
                });
        });
    };

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const mail = params.get('mail');
        const userType = params.get('userType');

        if (mail) {
            form.setFieldValue(LOGIN_FIELD_NAME, mail);
            setLoginValue(mail);
        }
        if (userType) {
            dispatch(setUserType(userType));
        }
    }, [dispatch, form]);

    return (
        <>
            <AuthSharedWrapper>
                <FormSection>
                    <ClientLogo />

                    <Form form={form} onFinish={handleSubmit} onFieldsChange={onFieldsChange}>
                        <InputWrapper>
                            <InputLabel>{LOGIN_LABEL}</InputLabel>
                            <Form.Item
                                noStyle
                                name={LOGIN_FIELD_NAME}
                                validateTrigger={['onBlur']}
                                rules={[
                                    { required: true, message: EMAIL_REQUIRE_MSG },
                                    { type: 'email', message: EMAIL_VALID_ERROR_MSG },
                                ]}
                            >
                                <BaseInput
                                    placeholder="Candidate@domain.com"
                                    value={loginValue}
                                    onChange={(e) => setLoginValue(e.target.value)}
                                    suffixIcon={loginHasError ? <ExclamationCircleFilled /> : null}
                                    hasError={loginHasError}
                                    data-cy="registration-input"
                                />
                            </Form.Item>
                            <HintText $showByDefault={loginHasError} className="has-error">
                                {getFieldError(LOGIN_FIELD_NAME, formFields)}
                            </HintText>
                        </InputWrapper>

                        {renderChangePasswordInput(
                            PASSWORD_REQUIRED_MSG,
                            PASSWORD_FIELD_NAME,
                            PASSWORD_FIELD_NAME,
                            PASSWORD_LABEL,
                            t`Enter your password`,
                            formFields,
                            isPasswordType,
                            setIsPasswordType,
                        )}

                        <Form.Item noStyle>
                            <BaseButton
                                loading={isCheckNotActivatedUserStatusPending}
                                className="full-width"
                                htmlType="submit"
                                data-cy="registration-button"
                            >
                                {t`Log in`}
                            </BaseButton>
                        </Form.Item>
                    </Form>

                    <BaseDivider />

                    <BaseRow justify="space-between" align="middle" style={{ width: '100%' }}>
                        <BaseRow align="middle" justify="start">
                            <LocaleSwitcher />
                        </BaseRow>
                        <BaseRow align="middle" justify="end">
                            <PasswordSection
                                onClick={() => {
                                    const isLoginValid = isEmailValid(form.getFieldValue(LOGIN_FIELD_NAME));
                                    setIsValidLogin(isLoginValid);

                                    if (isLoginValid) {
                                        setIsResetModalOpen(true);
                                    }
                                }}
                            >
                                {t`Reset password`}
                            </PasswordSection>
                        </BaseRow>
                    </BaseRow>

                    {!isValidLogin && <InvalidEmailErrorMsg>{t`Provide a valid login`}</InvalidEmailErrorMsg>}
                </FormSection>

                <FooterLogoSection>
                    <FooterLogo />
                </FooterLogoSection>
            </AuthSharedWrapper>

            <ResetPasswordModal open={isResetModalOpen} email={loginValue} onClose={() => setIsResetModalOpen(false)} />
        </>
    );
};

export default Register;
